import {
  BabyDiet,
  BacteriaAttention,
  BacteriaAttentionCategory,
  BacteriaReason,
  BacteriaReport,
  ConcernReason,
  RecommendationByCategory,
  RecommendationCategory,
  RecommendationType,
  ReportRecommendation,
  ReportSummary,
} from '@snapi/types';
import { ReportBacteriaAttentionUtils } from './report-bacteria-attention-utils';

const GutBalanceRecommendations: ReportRecommendation[] = [
  {
    id: '50564078-8440-4bb5-bb8b-927021f5c6e1',
    type: RecommendationType.short,
    category: RecommendationCategory.food,
    name: 'Increase yogurt',
  },
  {
    id: 'e7416b6b-9fd7-46c4-8149-b9b60b20b008',
    type: RecommendationType.short,
    category: RecommendationCategory.mother,
    name: 'Increase fermented foods',
  },
  {
    id: 'ea3c7517-73f7-4e75-820c-2352d771d470',
    type: RecommendationType.short,
    category: RecommendationCategory.food,
    name: 'Increase fiber',
  },
  // {
  //   id: '98024adb-a0ad-434a-af3a-f5dc27af3ad7',
  //   type: RecommendationType.short,
  //   category: RecommendationCategory.supplements,
  //   name: 'Levilactobacillus',
  // },
  // {
  //   id: '2c8f398b-498f-4b7e-b657-21ae2d584936',
  //   type: RecommendationType.short,
  //   category: RecommendationCategory.supplements,
  //   name: 'Lacticaseibacillus',
  // },
].map((recommendation) => ({
  reason: { concern: 'balance' },
  recommendation,
  score: 50,
}));

const DiversityRecommendations: ReportRecommendation[] = [
  {
    id: '9cdbe3f5-05f2-4f49-8fb6-3c6cdbf71494',
    type: RecommendationType.short,
    category: RecommendationCategory.food,
    name: 'Increase inulin',
  },
  {
    id: 'ea3c7517-73f7-4e75-820c-2352d771d470',
    type: RecommendationType.short,
    category: RecommendationCategory.food,
    name: 'Increase fiber',
  },
  {
    id: 'e7416b6b-9fd7-46c4-8149-b9b60b20b008',
    type: RecommendationType.short,
    category: RecommendationCategory.mother,
    name: 'Increase fermented foods',
  },
  // {
  //   id: 'bc858424-06df-443b-b216-6805244d11f7',
  //   type: RecommendationType.short,
  //   category: RecommendationCategory.supplements,
  //   name: 'Bifidobacterium animalis',
  // },
  // {
  //   id: 'b450fd60-a9da-4272-aec0-98972db9bf04',
  //   type: RecommendationType.short,
  //   category: RecommendationCategory.supplements,
  //   name: 'Lactobacillus acidophilus',
  // },
  // {
  //   id: '7bc88eb9-723b-433e-9a8d-1dcd4eadd725',
  //   type: RecommendationType.short,
  //   category: RecommendationCategory.supplements,
  //   name: 'Lactobacillus rhamnosus',
  // },
].map((recommendation) => ({
  reason: { concern: 'diversity' },
  recommendation,
  score: 50,
}));

const removeOverlappingByRecommendationAndReason = (
  selected: ReportRecommendation,
  recommendationByCategory: RecommendationByCategory
) => {
  Object.keys(recommendationByCategory).forEach((category) => {
    recommendationByCategory[category as RecommendationCategory] =
      recommendationByCategory[category as RecommendationCategory].filter(
        ({ recommendation, reason }) => {
          if (recommendation.name === selected.recommendation.name) {
            return false;
          }

          if (
            (reason as ConcernReason).concern &&
            (reason as ConcernReason).concern ===
              (selected.reason as ConcernReason).concern
          ) {
            return false;
          }

          if (
            (reason as BacteriaReason).name &&
            (reason as BacteriaReason).name ===
              (selected.reason as BacteriaReason).name
          ) {
            return false;
          }

          return true;
        }
      );
  });
};

const removeOverlappingByRecommendation = (
  selected: ReportRecommendation,
  recommendationByCategory: RecommendationByCategory
) => {
  Object.keys(recommendationByCategory).forEach((category) => {
    recommendationByCategory[category as RecommendationCategory] =
      recommendationByCategory[category as RecommendationCategory].filter(
        ({ recommendation }) => {
          if (recommendation.name === selected.recommendation.name) {
            return false;
          }
          return true;
        }
      );
  });
};

const suggestRecommendation = (
  recommendationByCategory: RecommendationByCategory,
  category: RecommendationCategory
): ReportRecommendation | null => {
  if (recommendationByCategory[category].length === 0) {
    return null;
  }
  const selected = recommendationByCategory[category][0];

  removeOverlappingByRecommendationAndReason(
    selected,
    recommendationByCategory
  );

  return selected;
};

const extractRecommendationForWeek = (
  recommendationByCategory: RecommendationByCategory,
  categories: RecommendationCategory[]
): ReportRecommendation[] =>
  categories
    .map((category) =>
      suggestRecommendation(recommendationByCategory, category)
    )
    .filter((rec): rec is ReportRecommendation => rec !== null);

const groupRecommendationsByCategory = (
  summary: ReportSummary,
  bacteria: BacteriaReport[]
): RecommendationByCategory => {
  const bacteriaByName: { [key in string]: BacteriaAttention } =
    ReportBacteriaAttentionUtils.pick(summary).reduce(
      (agg, bacteria) => Object.assign(agg, { [bacteria.name]: bacteria }),
      {}
    );

  const bacteriaRecommendations: ReportRecommendation[] = bacteria.flatMap(
    ({
      threshold: {
        research: { id, name, taxonomy },
        recommendations,
      },
    }) => {
      const bacteria = bacteriaByName[name];
      if (!bacteria) {
        return [];
      }

      return recommendations.map<ReportRecommendation>(
        ({ recommendation }) => ({
          recommendation,
          reason: {
            id,
            name,
            category: bacteria.category,
            taxonomy,
            action: bacteria.action,
          },
          score: bacteria.percentage,
        })
      );
    }
  );

  const recommendationByCategory: RecommendationByCategory = {
    [RecommendationCategory.food]: [],
    // [RecommendationCategory.supplements]: [],
    [RecommendationCategory.mother]: [],
    // [RecommendationCategory.lifestyle]: [],
  };

  bacteriaRecommendations
    .concat(summary.balance.good ? [] : GutBalanceRecommendations)
    .concat(summary.diversity.good ? [] : DiversityRecommendations)
    .forEach((recommendation) => {
      recommendationByCategory[recommendation.recommendation.category]?.push(
        recommendation
      );
    });

  Object.values(recommendationByCategory).forEach((recommendations) => {
    recommendations.sort((a, b) => {
      if (b.recommendation.type === a.recommendation.type) {
        return b.score - a.score;
      }
      if (b.recommendation.type === RecommendationType.extended) {
        return 1;
      } else if (a.recommendation.type === RecommendationType.extended) {
        return -1;
      }
      return b.score - a.score;
    });
  });

  return recommendationByCategory;
};

const getCategoryPriorities = (diet: BabyDiet[]) => {
  if (diet.length === 1) {
    switch (diet[0]) {
      case BabyDiet.Breastmilk:
        // - Breastfed baby = breastfed nutrition, lifestyle, supplements, food to consider for solid (to tag baby nutrition)
        return [
          RecommendationCategory.mother,
          // RecommendationCategory.lifestyle,
          // RecommendationCategory.supplements,
          RecommendationCategory.food,
        ];
      case BabyDiet.Solids:
        // - Solids only = baby nutrition, lifestyle, supplements
        return [
          RecommendationCategory.food,
          // RecommendationCategory.lifestyle,
          // RecommendationCategory.supplements,
        ];
      case BabyDiet.Formula:
        // - Only Formula Fed = lifestyle, food to consider for solid (to tag baby nutrition), supplements
        return [
          // RecommendationCategory.lifestyle,
          RecommendationCategory.food,
          // RecommendationCategory.supplements,
        ];
    }
  }

  if (diet.includes(BabyDiet.Breastmilk) && diet.includes(BabyDiet.Solids)) {
    // - Mix: breastfed + solids = breastfed nutrition, lifestyle, supplements, baby nutrition
    return [
      RecommendationCategory.mother,
      // RecommendationCategory.lifestyle,
      // RecommendationCategory.supplements,
      RecommendationCategory.food,
    ];
  }

  if (diet.includes(BabyDiet.Breastmilk)) {
    return [
      RecommendationCategory.mother,
      // RecommendationCategory.lifestyle,
      // RecommendationCategory.supplements,
      RecommendationCategory.food,
    ];
  }

  return [
    RecommendationCategory.food,
    // RecommendationCategory.supplements,
    // RecommendationCategory.lifestyle,
  ];
};

const extractKeyBacteriaRecommendations = (
  diet: BabyDiet[],
  recommendationByCategory: RecommendationByCategory
): ReportRecommendation[] => {
  const category =
    diet.includes(BabyDiet.Solids) || !diet.includes(BabyDiet.Breastmilk)
      ? RecommendationCategory.food
      : RecommendationCategory.mother;

  const keyBacteriaRecommendations = recommendationByCategory[category].filter(
    (rec) =>
      (rec.reason as BacteriaReason)?.category === BacteriaAttentionCategory.key
  );

  const keyBacteriaByName = keyBacteriaRecommendations.reduce<{
    [key in string]: ReportRecommendation;
  }>((agg, rec) => {
    const bacteriaName = (rec.reason as BacteriaReason).name;

    const hasBacteriaRec = agg[bacteriaName];
    const alreadyRecommended = Object.values(agg).find(
      ({ recommendation: { name } }) => rec.recommendation.name === name
    );

    if (hasBacteriaRec || alreadyRecommended) {
      return agg;
    } else {
      removeOverlappingByRecommendation(rec, recommendationByCategory);
      return Object.assign(agg, { [bacteriaName]: rec });
    }
  }, {});

  return Object.values(keyBacteriaByName);
};

const extract = (
  diet: BabyDiet[],
  summary: ReportSummary,
  bacteria: BacteriaReport[]
): ReportRecommendation[] => {
  const recommendationByCategory: RecommendationByCategory =
    groupRecommendationsByCategory(summary, bacteria);

  const categoryPriorities = getCategoryPriorities(diet);

  const keyBacteriaFoodRecommendations = extractKeyBacteriaRecommendations(
    diet,
    recommendationByCategory
  );

  const weeklyRecommendations: ReportRecommendation[][] = [
    keyBacteriaFoodRecommendations,
  ];

  let recommendations: ReportRecommendation[] = [];
  do {
    recommendations = extractRecommendationForWeek(
      recommendationByCategory,
      categoryPriorities
    );
    if (recommendations.length > 0) {
      weeklyRecommendations.push(recommendations);
    }
  } while (recommendations.length > 0);

  return weeklyRecommendations.flatMap((rec) => rec);
};

export const ReportRecommendationUtils = {
  extract,
  groupRecommendationsByCategory,
};
